import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { realtime } from './realtime';
import { seedPreferencesFromServer } from './hooks/userSettings';

const SESSION_KEY = ['session'] as const;

// Shared query definition for the current session (/v5/auth/me → user | null).
// Both useSession (render-time) and the route guards (beforeLoad, via
// queryClient.ensureQueryData) reference this, so the guard's fetch populates
// the same cache entry useSession reads — one /v5/auth/me per navigation
// instead of two (audit X19). 401 resolves to null (logged out), not a throw.
export const sessionQueryOptions = {
  queryKey: SESSION_KEY,
  queryFn: async () => {
    const { data, error, response } = await api.GET('/v5/auth/me');
    if (response.status === 401) return null;
    if (error) throw error;
    return data ?? null;
  },
} as const;

export function useSession() {
  return useQuery(sessionQueryOptions);
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { username: string; password: string }) => {
      const { data, error } = await api.POST('/v5/auth/login', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SESSION_KEY });
      // Seed the localStorage prefs (theme/sound) from the server ONCE, on the
      // login transition — never on a refresh, so localStorage stays the
      // working copy and a chosen theme survives reload. Fire-and-forget.
      void seedPreferencesFromServer();
    },
    // login.tsx renders its own "Login failed" alert from login.isError.
    meta: { suppressGlobalError: true },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Best-effort server logout: even if it errors (already-expired cookie,
      // network blip) the user asked to leave, so we still tear everything down.
      try {
        await api.POST('/v5/auth/logout');
      } catch {
        /* fall through to local teardown */
      }
    },
    // Full teardown so NOTHING from the logged-in session survives — no cached
    // chats/notifications/presence, no open socket, no router/React state.
    // Mirrors the identity-swap teardown in adminImpersonate.ts: close the
    // realtime socket, drop the whole query cache, then HARD-navigate to the
    // landing page (a full page load re-bootstraps the SPA from a clean slate;
    // the public landing at '/' shows the logged-out marketing view).
    onSettled: () => {
      realtime.close();
      qc.clear();
      if (typeof window !== 'undefined') window.location.assign('/');
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (emailOrUsername: string) => {
      // Always succeeds even if the email is unknown — the API returns 204
      // to avoid leaking which addresses are registered.
      const { error } = await api.POST('/v5/auth/password-reset', {
        body: { emailOrUsername },
      });
      if (error) throw error;
    },
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: async (body: { token: string; newPassword: string }) => {
      const { error } = await api.POST('/v5/auth/password-reset/confirm', { body });
      if (error) throw error;
    },
    // reset.tsx renders its own inline error from reset.isError.
    meta: { suppressGlobalError: true },
  });
}

export function useResendEmailVerification() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { error } = await api.POST('/v5/auth/email/resend', { body: { email } });
      if (error) throw error;
    },
    // check-email.tsx renders its own inline error from resend.isError.
    meta: { suppressGlobalError: true },
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      username: string;
      password: string;
      email: string;
      firstname?: string | null;
      lastname?: string | null;
      mobile?: string | null;
      country?: string | null;
      region?: string | null;
      city?: string | null;
    }) => {
      const { error } = await api.POST('/v5/auth/register', {
        body: {
          username: body.username,
          password: body.password,
          email: body.email,
          firstname: body.firstname ?? null,
          lastname: body.lastname ?? null,
          gender: null,
          yy: null,
          mm: null,
          dd: null,
          mobile: body.mobile ?? null,
          country: body.country ?? null,
          region: body.region ?? null,
          city: body.city ?? null,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
    // register.tsx catches the rejection and renders field-level errors.
    meta: { suppressGlobalError: true },
  });
}

export function useConfirmEmailVerification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const { error } = await api.POST('/v5/auth/email/verify', { body: { token } });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}
